import DialogTitle from '@mui/material/DialogTitle';
import clsx from 'clsx';
import { Dialog, DialogActions, DialogContent, Grid } from '@mui/material';
import React, { Fragment, useState } from 'react';
import makeStyles from '@mui/styles/makeStyles';
import _ from 'lodash';
import AgyleButton from '../ui/buttons/agyleButton';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useSelector } from 'react-redux';
import { ctxSplitScreen } from '../../store/reducers/patientContext';

const useStyles = makeStyles((theme) => ({
    background: {
        backgroundColor: `${theme.palette.background.default} !important`,
    },
    bodyBackground: {
        backgroundColor: theme.palette.background.dialog,
    },
    intenseBlur: {
        backdropFilter: ({ blur }) => blur && `blur(${blur}px)`,
    },
    dialogTitle: {
        textAlign: 'center',
        padding: '10px',
        fontWeight: 'bold',
        fontSize: 'x-large',
    },
    dialogContent: {
        height: '100%',
    },
    noPadding: {
        padding: 0,
    },
    closeHeaderButton: {
        height: 24,
    },
    titleWithCloseButton: {
        fontSize: '20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        // padding: "10px 24px"
        paddingLeft: '24px',
    },
    warning: {
        backgroundColor: `${theme.palette.warning.main}!important`,
        color: theme.palette.warning.contrastText,
    },
    absolute: {
        position: 'absolute',
    },
}));

const AgyleDialog = (props) => {
    const classes = useStyles({ blur: props.blur });

    const [btn, setBtn] = useState([]);

    const splitScreen = useSelector(ctxSplitScreen);
    const disablePortal = props?.disablePortal ?? splitScreen?.enabled;

    return (
        <Dialog
            open={props.open}
            onClose={(event, reason) => {
                if (reason === 'backdropClick' && props.noBackDropClose) return;
                props.onClose?.(event, reason);
            }}
            maxWidth={props.width}
            disableEscapeKeyDown={props.disableEscapeKeyDown}
            onBackdropClick={props.onBackdropClick}
            fullWidth={props.fullWidth}
            fullScreen={props.fullScreen}
            className={clsx(props.blur && classes.intenseBlur)}
            style={{ position: disablePortal ? 'absolute' : '', ...props.style }}
            // sx={props.sx}
            disablePortal={disablePortal}
            BackdropProps={{
                classes: { root: clsx(disablePortal && classes.absolute) },
            }}
            PaperProps={{
                style: { zIndex: props.zIndex, ...props.paperPropsStyle },
            }}
            sx={{ zIndex: props.zIndex, ...props.sx }}
        >
            {!props.disableTitle && !props.onlyModal && (
                <>
                    {props.patientInfo && (
                        <DialogTitle style={{ padding: '5px 10px' }}>
                            <Grid container>
                                <Grid item xs={10}>
                                    {props.patientInfo?.patient?.firstName} {props.patientInfo?.patient?.familyName}
                                </Grid>

                                <Grid item xs={2}>
                                    <IconButton className={classes.innerMethods} variant="outlined" onClick={props.onClose}>
                                        <CloseIcon className={classes.smallerIcon} />
                                    </IconButton>
                                </Grid>

                                <Grid item xs={12} style={{ fontSize: 15 }}>
                                    {props.patientInfo?.patient?.hospitalNumber}
                                </Grid>
                            </Grid>
                        </DialogTitle>
                    )}
                    <DialogTitle
                        className={clsx(
                            classes.background,
                            classes.dialogTitle,
                            props.titleStyle,
                            props.closeButtonOnHeader && classes.titleWithCloseButton,
                            props.warning && classes.warning,
                        )}
                    >
                        {props.title}
                        {props.closeButtonOnHeader && (
                            <Tooltip title="Close">
                                <IconButton variant="outlined" onClick={props.onClose}>
                                    <CloseIcon />
                                </IconButton>
                            </Tooltip>
                        )}
                    </DialogTitle>
                </>
            )}

            <DialogContent
                className={clsx(
                    'scroll-normal-white',
                    classes.bodyBackground,
                    classes.dialogContent,
                    props.contentStyle,
                    props.noPadding && classes.noPadding,
                    props.darkContentBackground && classes.background,
                )}
                dividers={props.dividers}
            >
                <>
                    {props.open &&
                        React.Children.toArray(props.children).map((child) =>
                            React.isValidElement(child) ? React.cloneElement(child, { agyleDialogInsertButtons: setBtn }) : child,
                        )}
                </>
            </DialogContent>

            {!props.disableActions && !props.onlyModal && (
                <DialogActions className={clsx(classes.background, props.actionStyle)}>
                    {props.alignActionsCloseButtonToRight && <div style={{ flex: '1 0 0' }} />}
                    <div style={props.alignActionsToLeft || props.alignActionsCloseButtonToRight ? null : { flex: '1 0 0' }}>
                        {!props.disableActionsCloseButton && (
                            <AgyleButton style={{ marginRight: 16 }} onClick={props.onClose} colour={props.cancelColour}>
                                {props.cancelText}
                            </AgyleButton>
                        )}
                    </div>

                    {!_.isEmpty(props.buttons) &&
                        props.buttons.map((element, index) => element && <Fragment key={index}>{element}</Fragment>)}

                    {!_.isEmpty(btn) && btn.map((element, index) => <Fragment key={index}>{element}</Fragment>)}

                    {!props.disableSave && (
                        <AgyleButton
                            onClick={props.onSave}
                            disabled={props.disableSubmit}
                            variant="contained"
                            colour={props.warning ? 'warning' : ''}
                            loading={props.pendingSubmit}
                        >
                            {props.submitText}
                        </AgyleButton>
                    )}
                </DialogActions>
            )}
        </Dialog>
    );
};

export default AgyleDialog;

AgyleDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
    children: PropTypes.any,
    disableEscapeKeyDown: PropTypes.bool,
    fullWidth: PropTypes.bool,
    fullScreen: PropTypes.bool,
    titleStyle: PropTypes.string,
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    disableTitle: PropTypes.bool,
    contentStyle: PropTypes.string,
    dividers: PropTypes.bool,
    disableActions: PropTypes.bool,
    cancelText: PropTypes.string,
    cancelColour: PropTypes.string,
    buttons: PropTypes.array,
    disableSave: PropTypes.bool,
    disableSubmit: PropTypes.bool,
    submitText: PropTypes.string,
    onlyModal: PropTypes.bool,
    closeButtonOnHeader: PropTypes.bool,
    warning: PropTypes.bool,
    disableActionsCloseButton: PropTypes.bool,
    alignActionsToLeft: PropTypes.bool,
    alignActionsCloseButtonToRight: PropTypes.bool,
    patientInfo: PropTypes.object,
    noPadding: PropTypes.bool,
    blur: PropTypes.oneOfType([PropTypes.bool, PropTypes.number]),
    darkContentBackground: PropTypes.bool,
    disablePortal: PropTypes.bool,
    zIndex: PropTypes.number,
    pendingSubmit: PropTypes.bool,
};

AgyleDialog.defaultProps = {
    width: 'sm',
    disableEscapeKeyDown: true,
    fullWidth: false,
    fullScreen: false,
    titleStyle: null,
    contentStyle: null,
    title: 'Title',
    disableTitle: false,
    dividers: true,
    disableActions: false,
    disableSave: false,
    disableSubmit: false,
    onlyModal: false,
    cancelText: 'Close',
    cancelColour: '',
    submitText: 'Submit',
    buttons: [],
    closeButtonOnHeader: false,
    alignActionsCloseButtonToRight: false,
    alignActionsToLeft: false,
    disableActionsCloseButton: false,
    warning: false,
    blur: false,
    noPadding: false,
    darkContentBackground: false,
    disablePortal: null,
    zIndex: 1300,
    pendingSubmit: false,
};
